import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown, MapPin, DollarSign, Camera, Truck, Heart, FileText, Phone, Mail } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FAQ_SECTION_META = [
  { key: "general", title: "General Questions About GlamBooth Cayman" },
  { key: "pricing", title: "Pricing, Packages & Booking" },
  { key: "features", title: "Booth Features & Experience" },
  { key: "setup", title: "Setup, Space & Service Areas" },
  { key: "weddings", title: "Weddings & Corporate Events" },
  { key: "policies", title: "Policies & Contact" },
];

const buildSchema = (faqs) =>
  JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.schema_text || (f.bullets?.length ? `${f.answer} ${f.bullets.join("; ")}.` : f.answer),
      },
    })),
  });

const ICONS = { general: MapPin, pricing: DollarSign, features: Camera, setup: Truck, weddings: Heart, policies: FileText };

const FaqItem = ({ item, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-black/8 rounded-2xl bg-white overflow-hidden" data-testid={`faq-item-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`faq-question-${id}`}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-[#f8eef6]/60 transition-colors"
      >
        <h3 className="font-semibold text-[#121212] text-[15px] md:text-base">{item.q}</h3>
        <ChevronDown size={18} className={`shrink-0 text-[#c9006f] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#121212]/70 text-sm md:text-[15px] leading-[1.8]" data-testid={`faq-answer-${id}`}>
              <p>{item.a}</p>
              {item.bullets && (
                <ul className="mt-3 space-y-1.5 list-none">
                  {item.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="text-[#c9006f] mt-0.5">✦</span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/faqs`).then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Photo Booth Rental Cayman Islands | FAQs, Pricing & Packages – GlamBooth Cayman";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const prevDesc = meta.content;
    meta.content =
      "Everything you need to know about renting a photo booth in Grand Cayman — packages from $400, custom backdrops, props, instant sharing, 360 video & more. Call (345) 328-6172.";
    return () => {
      document.title = "GlamBooth Cayman | Premium Photo Booth Rentals";
      meta.content = prevDesc || "";
    };
  }, []);

  const sections = FAQ_SECTION_META.map((m) => ({
    ...m,
    items: faqs
      .filter((f) => f.section === m.key)
      .map((f) => ({ q: f.question, a: f.answer, bullets: f.bullets?.length ? f.bullets : undefined })),
  })).filter((s) => s.items.length > 0);

  return (
    <div data-testid="faq-page">
      {faqs.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildSchema(faqs) }} />
      )}
      <Navbar />
      <header className="bg-[#3c003a] relative grain pt-40 pb-16">
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-[#ffc4e4] text-sm mb-8 transition-colors" data-testid="faq-back-link">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-white text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.1] mb-6"
          >
            Frequently Asked Questions — <em className="text-[#c9006f]">Photo Booth Rentals</em> in the Cayman Islands
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9 }}
            className="text-white/65 text-base leading-[1.8]"
          >
            Planning an event in Grand Cayman? Below you'll find answers to the most common questions
            about GlamBooth Cayman's photo booth rentals — pricing, packages, backdrops, props, setup,
            and service areas across the Cayman Islands. Still have a question? Call or WhatsApp us at{" "}
            <a href="https://wa.me/13453286172" className="text-[#ffc4e4] underline decoration-[#c9006f]">+1 (345) 328-6172</a> or email{" "}
            <a href="mailto:info@glamboothcayman.com" className="text-[#ffc4e4] underline decoration-[#c9006f]">info@glamboothcayman.com</a>.
          </motion.p>
        </div>
      </header>

      <main className="bg-[#fafaf7] py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-14">
          {sections.map((section) => {
            const Icon = ICONS[section.key];
            return (
              <section key={section.key} data-testid={`faq-section-${section.key}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#91008d]/10 text-[#91008d]">
                    <Icon size={19} strokeWidth={1.8} />
                  </span>
                  <h2 className="font-serif text-[#121212] text-2xl md:text-3xl">{section.title}</h2>
                </div>
                <div className="space-y-3">
                  {section.items.map((item, i) => (
                    <FaqItem key={i} item={item} id={`${section.key}-${i}`} />
                  ))}
                </div>
              </section>
            );
          })}

          <section className="bg-[#3c003a] rounded-3xl relative grain p-10 text-center overflow-hidden" data-testid="faq-cta">
            <div className="relative z-10">
              <h2 className="font-serif text-white text-2xl md:text-3xl mb-4">
                Still have a <em className="text-[#c9006f]">question?</em>
              </h2>
              <p className="text-white/60 text-sm md:text-base mb-8 max-w-lg mx-auto">
                We're happy to help you plan the perfect booth experience for your event.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/" state={{ scrollTo: "#contact" }} className="btn-gold rounded-full px-8 py-3.5 text-sm font-bold" data-testid="faq-book-btn">
                  Book Your GlamBooth
                </Link>
                <a href="tel:+13453286172" className="btn-outline-glow rounded-full px-8 py-3.5 text-sm font-bold text-white border border-white/30 inline-flex items-center gap-2">
                  <Phone size={15} /> +1 (345) 328-6172
                </a>
                <a href="mailto:info@glamboothcayman.com" className="btn-outline-glow rounded-full px-8 py-3.5 text-sm font-bold text-white border border-white/30 inline-flex items-center gap-2">
                  <Mail size={15} /> Email Us
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
